import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
// Import PhoneInput CSS first, then our custom CSS to override problematic styles
import 'react-phone-number-input/style.css';
import './JobApplicationForm.css';
import PublicHeader from './shared/PublicHeader';
import PublicFooter from './shared/PublicFooter';

/**
 * Public Job Application Form
 * Allows anyone to apply for a job without authentication
 */
const JobApplicationForm = () => {
  const { slugOrId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    experienceYears: '',
    linkedinUrl: '',
    githubUrl: '',
    portfolioUrl: '',
    hackeroneUrl: '',
    bugcrowdUrl: '',
    intigritiUrl: '',
    yeswehackUrl: '',
    hackenproofUrl: '',
    otherBugBountyUrl: '',
    coverLetter: '',
    source: 'direct',
    referredBy: '',
    otherSource: ''
  });

  const [customResponses, setCustomResponses] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [defaultPhoneCountry, setDefaultPhoneCountry] = useState('IN'); // Default to India

  useEffect(() => {
    loadJobPosting();
    autoPopulateLocation();
  }, [slugOrId]);

  const autoPopulateLocation = async () => {
    try {
      // Try to get location from IP geolocation API
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      if (data.city && data.country_name) {
        const location = `${data.city}, ${data.country_name}`;
        setFormData(prev => ({
          ...prev,
          location: location
        }));
      }

      // Set default phone country code from detected country
      // ipapi.co returns country_code as 2-letter ISO code (e.g., "IN", "US", "GB")
      if (data.country_code) {
        const countryCode = data.country_code.toUpperCase();
        // react-phone-number-input uses ISO 3166-1 alpha-2 codes, which matches ipapi.co format
        setDefaultPhoneCountry(countryCode);
        console.log(`[JobApplicationForm] Auto-detected country code: ${countryCode} from IP`);
      } else {
        // Fallback to India if country code not detected
        setDefaultPhoneCountry('IN');
        console.log('[JobApplicationForm] Using default country code: IN (India)');
      }
    } catch (err) {
      console.log('Could not auto-populate location:', err);
      // Silently fail - location is optional, default to India
      setDefaultPhoneCountry('IN');
    }
  };

  const loadJobPosting = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/jobs/${slugOrId}/public`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Job posting not found');
      }

      setJob(data.job);

      // Initialize custom responses
      if (data.job.customQuestions && data.job.customQuestions.length > 0) {
        const responses = {};
        data.job.customQuestions.forEach((q, idx) => {
          responses[idx] = q.type === 'multiselect' ? [] : '';
        });
        setCustomResponses(responses);
      }
    } catch (err) {
      console.error('Failed to load job posting:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeError(null);

    if (!file) {
      setResumeFile(null);
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/markdown'
    ];

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.markdown'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      setResumeError('Only PDF, DOC, DOCX, TXT, and Markdown files are allowed');
      setResumeFile(null);
      e.target.value = '';
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setResumeError('File size must be less than 5MB');
      setResumeFile(null);
      e.target.value = '';
      return;
    }

    setResumeFile(file);
  };

  const handleCustomResponseChange = (index, value, isMultiSelect = false) => {
    setCustomResponses(prev => {
      if (isMultiSelect) {
        const current = prev[index] || [];
        const updated = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        return { ...prev, [index]: updated };
      }
      return { ...prev, [index]: value };
    });
  };

  const validateBugBountyUrl = (url, platform) => {
    if (!url) return true; // Optional field

    const platformValidations = {
      linkedinUrl: /^https?:\/\/(www\.)?linkedin\.com\//i,
      githubUrl: /^https?:\/\/(www\.)?github\.com\//i,
      hackeroneUrl: /^https?:\/\/(www\.)?hackerone\.com\//i,
      bugcrowdUrl: /^https?:\/\/(www\.)?bugcrowd\.com\//i,
      intigritiUrl: /^https?:\/\/(www\.)?intigriti\.com\//i,
      yeswehackUrl: /^https?:\/\/(www\.)?yeswehack\.com\//i,
      hackenproofUrl: /^https?:\/\/(www\.)?hackenproof\.com\//i
    };

    if (platformValidations[platform]) {
      return platformValidations[platform].test(url);
    }

    // For otherBugBountyUrl, just validate it's a proper URL
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone is mandatory
    if (!formData.phone) {
      setError('Phone number is required');
      return;
    }
    if (!isValidPhoneNumber(formData.phone)) {
      setError('Please enter a valid phone number with country code');
      return;
    }

    // Validate bug bounty URLs
    const urlFields = [
      { field: 'linkedinUrl', name: 'LinkedIn' },
      { field: 'githubUrl', name: 'GitHub' },
      { field: 'hackeroneUrl', name: 'HackerOne' },
      { field: 'bugcrowdUrl', name: 'Bugcrowd' },
      { field: 'intigritiUrl', name: 'Intigriti' },
      { field: 'yeswehackUrl', name: 'YesWeHack' },
      { field: 'hackenproofUrl', name: 'HackenProof' },
      { field: 'otherBugBountyUrl', name: 'Bug Bounty Platform' }
    ];

    for (const { field, name } of urlFields) {
      if (formData[field] && !validateBugBountyUrl(formData[field], field)) {
        setError(`Please enter a valid ${name} profile URL`);
        return;
      }
    }

    // Validate required bug bounty profiles
    if (job.applicationFieldConfig?.requireBugBountyProfiles) {
      const hasBugBountyProfile = formData.hackeroneUrl || formData.bugcrowdUrl ||
        formData.intigritiUrl || formData.yeswehackUrl || formData.hackenproofUrl || formData.otherBugBountyUrl;
      if (!hasBugBountyProfile) {
        setError('At least one bug bounty profile is required');
        return;
      }
    }

    // Validate "Other" source
    if (formData.source === 'other' && !formData.otherSource.trim()) {
      setError('Please specify where you heard about this position');
      return;
    }

    // Validate required custom questions
    if (job.customQuestions && job.customQuestions.length > 0) {
      for (let i = 0; i < job.customQuestions.length; i++) {
        const question = job.customQuestions[i];
        if (question.required) {
          const response = customResponses[i];
          if (!response || (Array.isArray(response) && response.length === 0)) {
            setError(`Please answer: ${question.question}`);
            return;
          }
        }
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      // Format custom responses for submission
      const formattedResponses = job.customQuestions?.map((q, idx) => ({
        question: q.question,
        answer: customResponses[idx]
      })) || [];

      // Prepare form data with file upload
      const submitData = new FormData();

      // Add candidate info as JSON string
      submitData.append('candidateInfo', JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        experienceYears: formData.experienceYears,
        linkedinUrl: formData.linkedinUrl,
        githubUrl: formData.githubUrl,
        portfolioUrl: formData.portfolioUrl,
        hackeroneUrl: formData.hackeroneUrl,
        bugcrowdUrl: formData.bugcrowdUrl,
        intigritiUrl: formData.intigritiUrl,
        yeswehackUrl: formData.yeswehackUrl,
        hackenproofUrl: formData.hackenproofUrl,
        otherBugBountyUrl: formData.otherBugBountyUrl
      }));

      submitData.append('coverLetter', formData.coverLetter);
      submitData.append('source', formData.source);
      if (formData.referredBy) {
        submitData.append('referredBy', formData.referredBy);
      }
      if (formData.source === 'other' && formData.otherSource.trim()) {
        submitData.append('otherSource', formData.otherSource.trim());
      }

      if (formattedResponses.length > 0) {
        submitData.append('customResponses', JSON.stringify(formattedResponses));
      }

      // Add resume file if provided
      if (resumeFile) {
        submitData.append('resume', resumeFile);
      }

      const response = await fetch(`/api/jobs/${slugOrId}/apply`, {
        method: 'POST',
        body: submitData
        // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle rate limiting and race condition errors with specific messages
        if (response.status === 429) {
          const retryMessage = data.retryAfter
            ? `Please wait ${data.retryAfter} seconds before trying again.`
            : 'Please wait a moment before trying again.';
          throw new Error(data.error || retryMessage);
        }
        
        // Handle unverified email error with resend option
        if (response.status === 400 && data.error && data.error.includes('unverified application')) {
          const error = new Error(data.error);
          error.isUnverifiedError = true;
          throw error;
        }
        
        throw new Error(data.error || 'Failed to submit application');
      }

      setSuccess(true);

      // Store application data in sessionStorage (for phone verification if needed later)
      // Security: Do NOT store emailToken - it should ONLY be in the email
      // User must manually click the link from their email/SMS
      if (data.applicationId) {
        sessionStorage.setItem('applicationData', JSON.stringify({
          applicationId: data.applicationId,
          requiresPhoneVerification: data.requiresPhoneVerification || false,
          phoneNumber: data.phoneNumber || formData.phone || ''
          // emailToken removed for security - only sent via email
        }));
      }

      // Show success message on the same page - DO NOT auto-redirect with token in URL
      // Security: User must click the verification link from their email/SMS
      setSuccess(true);
      setError(null);
      
      // Clear form data
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        location: '',
        linkedinUrl: '',
        githubUrl: '',
        portfolioUrl: '',
        hackeroneUrl: '',
        bugcrowdUrl: '',
        intigritiUrl: '',
        yeswehackUrl: '',
        hackenproofUrl: '',
        otherBugBountyUrl: '',
        coverLetter: '',
        source: 'direct',
        referredBy: '',
        otherSource: ''
      });
      setCustomResponses({});
      setResumeFile(null);
      setResumeError(null);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Failed to submit application:', err);
      // Extract error message properly from API response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to submit application. Please try again.';
      setError(errorMessage);
      
      // Show resend option if it's an unverified email error
      if (err.isUnverifiedError) {
        setShowResendOption(true);
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResending(true);
      setError(null);
      
      const response = await fetch('/api/jobs/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          jobId: job._id
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }
      
      setError(null);
      setSuccess(true);
      setShowResendOption(false);
      setResendSuccess(true);
      
    } catch (err) {
      console.error('Failed to resend verification:', err);
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <>
        <PublicHeader />
        <div className="application-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading application form...</p>
          </div>
        </div>
        <PublicFooter />
      </>
    );
  }

  if (error && !job) {
    return (
      <>
        <PublicHeader />
        <div className="application-container">
          <div className="error-state">
            <h1>Job Not Found</h1>
            <p>{error}</p>
            <button className="btn-primary" onClick={() => navigate('/jobs')}>
              View All Jobs
            </button>
          </div>
        </div>
        <PublicFooter />
      </>
    );
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Application Submitted - {job.title}</title>
        </Helmet>
        <PublicHeader />
        <div className="application-container">
          <div className="success-state">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1>Application Submitted Successfully!</h1>
            <p>Thank you for applying for the <strong>{job.title}</strong> position at {job.company || 'SecureLayer7'}.</p>
            
            <div style={{ 
              background: 'var(--info-light, #cfe2ff)', 
              border: '1px solid var(--info, #007bff)', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              margin: '1.5rem 0',
              textAlign: 'left'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: 'var(--info-dark, #084298)' }}>
                Next Steps - Verify Your Contact Information
              </h3>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--info-dark, #084298)', fontSize: '0.9375rem', lineHeight: '1.8' }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <strong>Email Verification:</strong> Check your email inbox (and spam folder) for a verification link. Click the link to verify your email address.
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <strong>Phone Verification:</strong> If you provided a phone number, you'll receive an SMS with a verification code and link. Enter the code to verify your phone number.
                </li>
                <li>
                  <strong>Important:</strong> Please complete both verifications to ensure we can contact you about your application status.
                </li>
              </ul>
            </div>

            <p style={{ marginTop: '1.5rem', color: 'var(--text-secondary, #6c757d)' }}>
              Once verified, we'll review your application and get back to you shortly. If your qualifications match our requirements, we'll send you an assessment to showcase your skills.
            </p>
            <div className="success-actions">
              <button className="btn-primary" onClick={() => navigate(`/jobs/${job.slug || slugOrId}`)}>
                Back to Job Posting
              </button>
              <button className="btn-secondary" onClick={() => navigate('/jobs')}>
                View More Jobs
              </button>
            </div>
          </div>
        </div>
        <PublicFooter />
      </>
    );
  }

  const pageTitle = `Apply for ${job.title} at ${job.company || 'SecureLayer7'}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`Apply for the ${job.title} position at ${job.company || 'SecureLayer7'}. ${job.location} - ${job.workMode} - ${job.employmentType?.replace('-', ' ')}.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`https://sechire.net/jobs/${job.slug || slugOrId}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://sechire.net/jobs/${job.slug || slugOrId}/apply`} />
        <meta property="og:title" content={`Apply for ${job.title} - ${job.company || 'SecureLayer7'}`} />
        <meta property="og:description" content={`Submit your application for the ${job.title} position at ${job.company || 'SecureLayer7'} in ${job.location}.`} />
        <meta property="og:image" content="https://securelayer7.net/images/logo.png" />
        <meta property="og:site_name" content="SecureLayer7 Careers" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Apply for ${job.title} - ${job.company || 'SecureLayer7'}`} />
        <meta name="twitter:description" content={`Submit your application for the ${job.title} position at ${job.company || 'SecureLayer7'}.`} />
      </Helmet>

      <PublicHeader />

      <main className="application-container">
        <div className="application-content">
          {/* Header */}
          <div className="application-header">
            <button className="back-button" onClick={() => navigate(`/jobs/${job.slug || slugOrId}`)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Job
            </button>
            <h1>Apply for {job.title}</h1>
            <p className="job-company">{job.company || 'SecureLayer7'} • {job.location}</p>
          </div>

          {/* Resend Success Message */}
          {resendSuccess && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '2px solid #10B981',
              color: '#065F46',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" style={{ flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                Verification email sent! Please check your inbox (and spam folder).
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="error-banner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div style={{ flex: 1 }}>
              {error}
                {showResendOption && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resending}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: resending ? 'not-allowed' : 'pointer',
                        fontWeight: '500',
                        fontSize: '14px'
                      }}
                    >
                      {resending ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Application Form */}
          <form onSubmit={handleSubmit} className="application-form">
            {/* Personal Information */}
            <section className="form-section">
              <h2>Personal Information</h2>
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    placeholder="John"
                    autoComplete="given-name"
                    tabIndex="1"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    placeholder="Doe"
                    autoComplete="family-name"
                    tabIndex="2"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="john.doe@example.com"
                    autoComplete="email"
                    tabIndex="3"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="phone">
                    Phone Number *
                  </label>
                  <PhoneInput
                    international
                    defaultCountry={defaultPhoneCountry}
                    value={formData.phone}
                    onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                    placeholder="Enter phone number"
                    className="phone-input-wrapper"
                    inputClassName="phone-input"
                    numberInputProps={{
                      id: 'phone',
                      required: true,
                      autoComplete: 'tel',
                      tabIndex: 4
                    }}
                    error={formData.phone && !isValidPhoneNumber(formData.phone) ? 'Invalid phone number' : undefined}
                  />
                  <small className="field-hint" style={{ marginTop: '0.25rem', display: 'block', color: '#6B7280', fontSize: '0.875rem' }}>
                    Include country code (e.g., +1 for US, +91 for India)
                  </small>
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="location">
                  Location {job.applicationFieldConfig?.requireLocation && '*'}
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required={job.applicationFieldConfig?.requireLocation}
                  placeholder="City, Country"
                />
                <small className="field-hint">Auto-detected from your IP address</small>
              </div>

              <div className="form-field">
                <label htmlFor="experienceYears">Years of experience *</label>
                <input
                  type="text"
                  id="experienceYears"
                  name="experienceYears"
                  inputMode="decimal"
                  value={formData.experienceYears}
                  onChange={handleInputChange}
                  required
                  pattern="^[0-9]{1,2}(\.[0-9])?\+?$"
                  placeholder="e.g. 0 for fresher, 2, or 3.5"
                  title="A number of years, for example 0, 2 or 3.5"
                />
                <small className="field-hint">Professional security experience; internships count. Enter 0 if you are starting out.</small>
              </div>
            </section>

            {/* Resume Upload */}
            <section className="form-section">
              <h2>Resume/CV {job.requireResume && '*'}</h2>
              <p className="section-description">Upload your resume to help us understand your experience</p>

              <div className="form-field">
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,.doc,.docx,.txt,.md,.markdown"
                    onChange={handleFileChange}
                    required={job.requireResume}
                    className="file-input"
                  />
                  <label htmlFor="resume" className="file-upload-label">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="file-upload-text">
                      {resumeFile ? resumeFile.name : 'Choose file or drag here'}
                    </span>
                    <span className="file-upload-hint">PDF, DOC, DOCX, TXT, or Markdown (max 5MB)</span>
                  </label>
                </div>

                {resumeFile && (
                  <div className="file-selected">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{resumeFile.name}</span>
                    <span className="file-size">({(resumeFile.size / 1024).toFixed(1)} KB)</span>
                    <button
                      type="button"
                      onClick={() => {
                        setResumeFile(null);
                        document.getElementById('resume').value = '';
                      }}
                      className="file-remove"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {resumeError && (
                  <div className="file-error">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {resumeError}
                  </div>
                )}
              </div>
            </section>

            {/* Professional Links */}
            <section className="form-section">
              <h2>Professional Profiles</h2>
              <div className="form-field">
                <label htmlFor="linkedinUrl">
                  LinkedIn Profile {job.applicationFieldConfig?.requireLinkedIn && '*'}
                </label>
                <input
                  type="url"
                  id="linkedinUrl"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  required={job.applicationFieldConfig?.requireLinkedIn}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
              <div className="form-field">
                <label htmlFor="githubUrl">
                  GitHub Profile {job.applicationFieldConfig?.requireGitHub && '*'}
                </label>
                <input
                  type="url"
                  id="githubUrl"
                  name="githubUrl"
                  value={formData.githubUrl}
                  onChange={handleInputChange}
                  required={job.applicationFieldConfig?.requireGitHub}
                  placeholder="https://github.com/yourusername"
                />
              </div>
              <div className="form-field">
                <label htmlFor="portfolioUrl">
                  Portfolio / Personal Website {job.applicationFieldConfig?.requirePortfolio && '*'}
                </label>
                <input
                  type="url"
                  id="portfolioUrl"
                  name="portfolioUrl"
                  value={formData.portfolioUrl}
                  onChange={handleInputChange}
                  required={job.applicationFieldConfig?.requirePortfolio}
                  placeholder="https://yourportfolio.com"
                />
              </div>
            </section>

            {/* Bug Bounty Profiles - Conditional based on job config */}
            {job.applicationFieldConfig?.showBugBountyProfiles && (
            <section className="form-section">
              <h2>Bug Bounty Profiles {job.applicationFieldConfig?.requireBugBountyProfiles && '*'}</h2>
              <p className="section-description">
                Share your bug bounty profiles to showcase your security research experience
                {job.applicationFieldConfig?.requireBugBountyProfiles && ' (at least one required)'}
              </p>

              <div className="form-field">
                <label htmlFor="hackeroneUrl">HackerOne Profile</label>
                <input
                  type="url"
                  id="hackeroneUrl"
                  name="hackeroneUrl"
                  value={formData.hackeroneUrl}
                  onChange={handleInputChange}
                  placeholder="https://hackerone.com/yourusername"
                />
              </div>

              <div className="form-field">
                <label htmlFor="bugcrowdUrl">Bugcrowd Profile</label>
                <input
                  type="url"
                  id="bugcrowdUrl"
                  name="bugcrowdUrl"
                  value={formData.bugcrowdUrl}
                  onChange={handleInputChange}
                  placeholder="https://bugcrowd.com/yourusername"
                />
              </div>

              <div className="form-field">
                <label htmlFor="intigritiUrl">Intigriti Profile</label>
                <input
                  type="url"
                  id="intigritiUrl"
                  name="intigritiUrl"
                  value={formData.intigritiUrl}
                  onChange={handleInputChange}
                  placeholder="https://intigriti.com/researcher/yourusername"
                />
              </div>

              <div className="form-field">
                <label htmlFor="yeswehackUrl">YesWeHack Profile</label>
                <input
                  type="url"
                  id="yeswehackUrl"
                  name="yeswehackUrl"
                  value={formData.yeswehackUrl}
                  onChange={handleInputChange}
                  placeholder="https://yeswehack.com/hunters/yourusername"
                />
              </div>

              <div className="form-field">
                <label htmlFor="hackenproofUrl">HackenProof Profile</label>
                <input
                  type="url"
                  id="hackenproofUrl"
                  name="hackenproofUrl"
                  value={formData.hackenproofUrl}
                  onChange={handleInputChange}
                  placeholder="https://hackenproof.com/profile/yourusername"
                />
              </div>

              <div className="form-field">
                <label htmlFor="otherBugBountyUrl">Other Bug Bounty Platform</label>
                <input
                  type="url"
                  id="otherBugBountyUrl"
                  name="otherBugBountyUrl"
                  value={formData.otherBugBountyUrl}
                  onChange={handleInputChange}
                  placeholder="https://other-platform.com/yourprofile"
                />
                <small className="field-hint">Any other bug bounty or security research platform</small>
              </div>
            </section>
            )}

            {/* Cover Letter */}
            {(job.requireCoverLetter || job.applicationFieldConfig?.requireCoverLetter) ? (
              <section className="form-section">
                <h2>Cover Letter *</h2>
                <div className="form-field">
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    required={job.requireCoverLetter}
                    rows="8"
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  />
                </div>
              </section>
            ) : (
              <section className="form-section">
                <h2>Cover Letter (Optional)</h2>
                <div className="form-field">
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    rows="8"
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  />
                </div>
              </section>
            )}

            {/* Custom Questions */}
            {job.customQuestions && job.customQuestions.length > 0 && (
              <section className="form-section">
                <h2>Additional Questions</h2>
                {job.customQuestions.map((question, idx) => (
                  <div key={idx} className="form-field">
                    <label>
                      {question.question}
                      {question.required && ' *'}
                    </label>

                    {question.type === 'text' && (
                      <input
                        type="text"
                        value={customResponses[idx] || ''}
                        onChange={(e) => handleCustomResponseChange(idx, e.target.value)}
                        required={question.required}
                      />
                    )}

                    {question.type === 'textarea' && (
                      <textarea
                        value={customResponses[idx] || ''}
                        onChange={(e) => handleCustomResponseChange(idx, e.target.value)}
                        required={question.required}
                        rows="4"
                      />
                    )}

                    {question.type === 'select' && (
                      <select
                        value={customResponses[idx] || ''}
                        onChange={(e) => handleCustomResponseChange(idx, e.target.value)}
                        required={question.required}
                      >
                        <option value="">Select an option...</option>
                        {question.options?.map((opt, optIdx) => (
                          <option key={optIdx} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}

                    {question.type === 'multiselect' && (
                      <div className="checkbox-group">
                        {question.options?.map((opt, optIdx) => (
                          <label key={optIdx} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={(customResponses[idx] || []).includes(opt)}
                              onChange={() => handleCustomResponseChange(idx, opt, true)}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </section>
            )}

            {/* How did you hear about us */}
            <section className="form-section">
              <h2>How did you hear about this position?</h2>
              <div className="form-field">
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                >
                  <option value="direct">Company Website</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="job_board">Job Board</option>
                  <option value="referral">Referral</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {formData.source === 'referral' && (
                <div className="form-field">
                  <label htmlFor="referredBy">Referred By</label>
                  <input
                    type="text"
                    id="referredBy"
                    name="referredBy"
                    value={formData.referredBy}
                    onChange={handleInputChange}
                    placeholder="Name of person who referred you"
                  />
                </div>
              )}

              {formData.source === 'other' && (
                <div className="form-field">
                  <label htmlFor="otherSource">Please specify *</label>
                  <input
                    type="text"
                    id="otherSource"
                    name="otherSource"
                    value={formData.otherSource}
                    onChange={handleInputChange}
                    placeholder="e.g. Naukri, Instagram, Twitter, etc."
                    required
                  />
                </div>
              )}
            </section>

            {/* Submit Button */}
            <div className="form-actions">
              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <div className="button-spinner"></div>
                    Submitting Application...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Application
                  </>
                )}
              </button>
              <p className="form-note">
                By submitting this application, you agree to our privacy policy and terms of service.
              </p>
            </div>
          </form>
        </div>
      </main>

      <PublicFooter />
    </>
  );
};

export default JobApplicationForm;
